/**
 * `AssetLibrary` — coluna esquerda do editor de playlist (TH-53378).
 *
 * Lista assets disponíveis em duas seções:
 *
 *   - **Artes personalizadas** (custom_arts) — listáveis via
 *     `GET /custom-arts` com filtros por loja/área. Cada card é
 *     arrastável (dnd-kit) para a sequência da direita.
 *   - **Artes geradas por IA** (product_arts) — *não* listáveis hoje;
 *     o backend não expõe `GET /product-arts`. A UX entra por
 *     "Auto-gerar a partir da área", que já vive no header do editor.
 *     Renderizamos um bloco explicativo no lugar da lista (em vez de
 *     deixar vazio, que parece bug).
 *
 * Filtros visíveis: Loja + Área. Tipo/produto foram retirados porque
 * neste escopo só existe um tipo de asset listável (custom_art) e
 * `product` não aplica a custom_arts.
 */

import { useDraggable } from "@dnd-kit/core";
import { ImageIcon, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Select } from "@/components/ui/Select";
import { cn } from "@/lib/utils";
import type { CustomArtRead } from "@/hooks/useCreateCustomArt";

export interface AssetLibraryOption {
  id: string;
  name: string;
}

export interface AssetLibraryProps {
  customArts: ReadonlyArray<CustomArtRead>;
  stores: ReadonlyArray<AssetLibraryOption>;
  areas: ReadonlyArray<AssetLibraryOption>;
  storeFilter: string;
  areaFilter: string;
  onStoreFilterChange: (id: string) => void;
  onAreaFilterChange: (id: string) => void;
  loading: boolean;
  error: boolean;
}

export function AssetLibrary({
  customArts,
  stores,
  areas,
  storeFilter,
  areaFilter,
  onStoreFilterChange,
  onAreaFilterChange,
  loading,
  error,
}: AssetLibraryProps) {
  return (
    <div className="flex h-full flex-col rounded-md border border-[var(--color-border)] bg-[var(--color-card)]">
      <header className="border-b border-[var(--color-border)] p-3">
        <h3 className="text-sm font-semibold">Biblioteca</h3>
        <p className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
          Arraste pra adicionar à playlist.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="grid gap-1">
            <label
              htmlFor="asset-store-filter"
              className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]"
            >
              Loja
            </label>
            <Select
              id="asset-store-filter"
              value={storeFilter}
              onChange={(e) => onStoreFilterChange(e.target.value)}
            >
              <option value="">Todas</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid gap-1">
            <label
              htmlFor="asset-area-filter"
              className="text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]"
            >
              Área
            </label>
            <Select
              id="asset-area-filter"
              value={areaFilter}
              onChange={(e) => onAreaFilterChange(e.target.value)}
            >
              <option value="">Todas</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-3">
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
            Artes personalizadas
          </h4>
          <div className="mt-2">
            {error ? (
              <p className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-2 py-1.5 text-xs text-[var(--color-destructive)]">
                erro ao carregar artes
              </p>
            ) : loading ? (
              <p className="text-xs text-[var(--color-muted-foreground)]">
                carregando…
              </p>
            ) : customArts.length === 0 ? (
              <p className="rounded-md border border-dashed border-[var(--color-border-strong)] px-2 py-3 text-center text-xs text-[var(--color-muted-foreground)]">
                Nenhuma arte personalizada nos filtros atuais.
              </p>
            ) : (
              <ul role="list" className="grid grid-cols-2 gap-2">
                {customArts.map((art) => (
                  <li key={art.id}>
                    <DraggableAssetCard art={art} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="mt-6">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
            Artes geradas por IA
          </h4>
          <div className="mt-2 rounded-md border border-dashed border-[var(--color-border-strong)] bg-[var(--color-background)] p-3">
            <div className="flex items-start gap-2 text-xs text-[var(--color-muted-foreground)]">
              <Sparkles
                className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-primary)]"
                aria-hidden="true"
              />
              <p>
                Artes de IA entram via &ldquo;Auto-gerar a partir da
                área&rdquo; — botão no topo do editor.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

interface DraggableAssetCardProps {
  art: CustomArtRead;
}

function DraggableAssetCard({ art }: DraggableAssetCardProps) {
  // `id` no formato `asset:custom:<uuid>` evita colisão com slides da
  // playlist (`slide:<uuid>`) no DndContext compartilhado.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `asset:custom:${art.id}`,
    data: {
      type: "asset",
      kind: "custom_art",
      art,
    },
  });

  const isWebUrl = /^https?:\/\//i.test(art.art_url);

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      role="button"
      tabIndex={0}
      aria-label={`Arrastar ${art.name}`}
      className={cn(
        "group flex w-full cursor-grab flex-col overflow-hidden rounded-md border border-[var(--color-border)]",
        "bg-[var(--color-background)] text-left transition-colors",
        "hover:border-[var(--color-primary)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]",
        isDragging && "opacity-50",
      )}
    >
      <div className="flex aspect-video items-center justify-center overflow-hidden bg-[var(--color-card)]">
        {isWebUrl ? (
          // Pointer-events-none impede que o browser comece a arrastar a
          // imagem nativa em vez de a div toda (drag fantasma).
          <img
            src={art.art_url}
            alt={art.name}
            className="pointer-events-none h-full w-full object-cover"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <ImageIcon
            className="h-6 w-6 text-[var(--color-muted-foreground)]"
            aria-hidden="true"
          />
        )}
      </div>
      <div className="flex flex-col gap-1 p-2">
        <p className="line-clamp-1 text-xs font-medium text-[var(--color-foreground)]">
          {art.name}
        </p>
        {!art.active ? (
          <Badge variant="muted" className="self-start">
            Inativa
          </Badge>
        ) : null}
      </div>
    </div>
  );
}
